"use client";

import React from "react";
import { FileText, Info } from "lucide-react";
import { Badge } from "../badge.js";
import { CalendarEvent } from "../../types/calendar.js";
import { CrmLinkButton } from "./CrmLinkButton.js";
import {
  EventFormSection,
  EventFormCollapsibleSection,
} from "./calendarinvites/shared/EventFormShell.js";
import DOMPurify from "dompurify";

interface EventDetailsTabProps {
  event: CalendarEvent;
  onEdit?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  isPageView?: boolean;
}

// Collapse the description behind progressive disclosure when it is long.
const LONG_DESCRIPTION_THRESHOLD = 280;

export function EventDetailsTab({
  event,
}: EventDetailsTabProps) {
  const sanitizeHtml = (htmlContent: string) => {
    return DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'a', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style']
    });
  };

  const renderDescription = (description: string) => {
    if (!description) return <p className="text-sm text-muted-foreground">No description available.</p>;

    const containsHtml = /<[^>]*>/.test(description);

    if (containsHtml) {
      // For HTML content, sanitize and render as HTML
      return (
        <div
          className="text-sm leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
        />
      );
    }

    // For plain text, convert newlines to <br> tags and detect links
    const formatDescription = (text: string) => {
      return text.split('\n').map((line, lineIndex) => {
        // Regular expression to detect URLs starting with https:// or http://
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = line.split(urlRegex);

        return (
          <React.Fragment key={lineIndex}>
            {parts.map((part, partIndex) => {
              if (urlRegex.test(part)) {
                return (
                  <a
                    key={partIndex}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline break-all"
                  >
                    {part}
                  </a>
                );
              }
              return part;
            })}
            {lineIndex < text.split('\n').length - 1 && <br />}
          </React.Fragment>
        );
      });
    };

    return (
      <div className="text-sm leading-relaxed">
        {formatDescription(description)}
      </div>
    );
  };

  const description = event.description || "";
  const isLongDescription = description.length > LONG_DESCRIPTION_THRESHOLD;

  const hasAdditionalDetails = Boolean(
    event.showAs || event.importance || event.sensitivity || event.isCancelled,
  );

  return (
    <div className="space-y-6">
      {/* Event Description — collapsed behind progressive disclosure when long */}
      {description && (
        isLongDescription ? (
          <EventFormCollapsibleSection
            title="Description"
            icon={FileText}
            defaultOpen={false}
          >
            <div className="max-h-80 always-visible-scrollbar pr-2">
              {renderDescription(description)}
            </div>
          </EventFormCollapsibleSection>
        ) : (
          <EventFormSection title="Description" icon={FileText}>
            {renderDescription(description)}
          </EventFormSection>
        )
      )}

      {/* Additional Details — 2-col grid using horizontal space */}
      {hasAdditionalDetails && (
        <EventFormSection title="Additional details" icon={Info}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
            {event.showAs && (
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="mb-1 text-xs text-muted-foreground">Show As</p>
                <p className="text-sm font-medium capitalize">{event.showAs}</p>
              </div>
            )}
            {event.importance && (
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="mb-1 text-xs text-muted-foreground">Importance</p>
                <p className="text-sm font-medium capitalize">{event.importance}</p>
              </div>
            )}
            {event.sensitivity && (
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="mb-1 text-xs text-muted-foreground">Sensitivity</p>
                <p className="text-sm font-medium capitalize">{event.sensitivity}</p>
              </div>
            )}
            {event.isCancelled && (
              <div className="sm:col-span-2">
                <Badge variant="destructive" className="text-sm">
                  Event Cancelled
                </Badge>
              </div>
            )}
          </div>
        </EventFormSection>
      )}

      {/* CRM Integration */}
      <CrmLinkButton
        description={event.description}
        authToken={undefined} // TODO: Pass auth token when available
      />
    </div>
  );
}
